import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import sports from "../../sports";
import styles from "./Greenlight.module.css";
import { posterBlobUrl } from "./posterCard";
import { MigrationModal, migrationUrl } from "../Migration/Migration";

const API = "https://crossovergames-8b4eb8e6663f.herokuapp.com/api/greenlight";

const TYPE_EMOJI = {
  director: "🎬",
  actor: "⭐",
  genre: "🎭",
  budget: "💰",
  location: "📍",
};

const TIER_COLORS = {
  S: "#facc15",
  A: "#16a34a",
  B: "#0ea5e9",
  C: "#9ca3af",
  D: "#f97316",
  F: "#dc2626",
};

function getPlayerId() {
  let id = localStorage.getItem("greenlight_player_id");
  if (!id) {
    id =
      "gl-" +
      Math.random().toString(36).slice(2, 10) +
      Date.now().toString(36);
    localStorage.setItem("greenlight_player_id", id);
  }
  return id;
}

function money(m) {
  if (m == null || Number.isNaN(m)) return "—";
  return m >= 1000 ? `$${(m / 1000).toFixed(2)}B` : `$${Math.round(m)}M`;
}

// Pick the best open slot for an asset, keeping the ungendered lead
// slot flexible for as long as possible.
function slotForAsset(asset, picks, slots) {
  const open = slots.filter((s) => !picks[s.id]);
  if (asset.type !== "actor") {
    return open.find((s) => s.accepts === asset.type)?.id || null;
  }
  const gendered = open.find(
    (s) => s.accepts === "actor" && s.gender && s.gender === asset.gender
  );
  if (gendered) return gendered.id;
  const lead = open.find((s) => s.accepts === "actor" && !s.gender);
  return lead ? lead.id : null;
}

// Cycles through pool names every 80ms (slot-reel style), then locks on the
// real value after `delay` ms with a bounce — mirrors the mobile SlotsScreen.
function useReel(realValue, pool, delay, started) {
  const [locked, setLocked] = useState(false);
  const [display, setDisplay] = useState(pool[0] || "");
  useEffect(() => {
    if (!started) return;
    setLocked(false);
    const iv = setInterval(() => {
      setDisplay(pool[Math.floor(Math.random() * pool.length)] || "???");
    }, 80);
    const to = setTimeout(() => {
      clearInterval(iv);
      setLocked(true);
    }, delay);
    return () => {
      clearInterval(iv);
      clearTimeout(to);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [started]);
  return [locked, locked ? realValue : display];
}

function TitleReel({ title, year, poster, pool, started }) {
  const [locked, display] = useReel(title, pool, 1000, started);
  return (
    <>
      <h2
        className={`${styles.movieTitle} ${locked ? styles.reelLocked : styles.titleCycling}`}
      >
        {display}{" "}
        {locked && <span className={styles.movieYear}>({year})</span>}
      </h2>
      {locked && poster && (
        <div className={styles.posterWrap}>
          <img className={styles.poster} src={poster} alt={title} />
        </div>
      )}
    </>
  );
}

function SpinAssetRow({ asset, pool, delay, started, target, onPick, typeTag }) {
  const [locked, display] = useReel(asset.name, pool, delay, started);
  return (
    <button
      className={`${styles.assetBtn} ${locked ? styles.reelLocked : ""}`}
      disabled={!locked || !target}
      onClick={() => onPick(asset)}
    >
      {locked && asset.img ? (
        <img className={styles.assetImg} src={asset.img} alt={asset.name} />
      ) : (
        <span className={styles.assetBadge}>
          {locked ? TYPE_EMOJI[asset.type] : "🎰"}
        </span>
      )}
      <span className={styles.assetInfo}>
        <span className={styles.assetType}>{typeTag}</span>
        <span className={locked ? "" : styles.reelCycling}>{display}</span>
      </span>
      {locked && <span className={styles.assetPlus}>+</span>}
    </button>
  );
}

export default function Greenlight() {
  const playerId = useMemo(getPlayerId, []);
  const [daily, setDaily] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("draft");
  const [round, setRound] = useState(0);
  const [picks, setPicks] = useState({}); // slotId -> asset
  const [pickOrder, setPickOrder] = useState([]); // [{slot, assetId}] in round order
  const [submitError, setSubmitError] = useState(null);
  const [playerName, setPlayerName] = useState(
    localStorage.getItem("greenlight_player_name") || ""
  );
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [leaderboard, setLeaderboard] = useState(null);
  const [metric, setMetric] = useState("score");
  const [copied, setCopied] = useState(false);
  const [spun, setSpun] = useState(false); // has the player spun this round?
  const [poster, setPoster] = useState(null); // generated share poster {url, blob}

  useEffect(() => {
    fetch(`${API}/daily`)
      .then((r) => r.json())
      .then((d) => {
        setDaily(d);
        const saved = localStorage.getItem(`greenlight_result_${d.date}`);
        if (saved) {
          setResult(JSON.parse(saved));
          setTab("results");
        }
      })
      .catch(() => setError("Could not load today's draft. Try again later."));
  }, []);

  useEffect(() => {
    if (tab !== "leaderboard" || !daily) return;
    setLeaderboard(null);
    fetch(`${API}/leaderboard?metric=${metric}`)
      .then((r) => r.json())
      .then(setLeaderboard)
      .catch(() => setLeaderboard({ leaderboard: [] }));
  }, [tab, metric, daily]);

  // build the movie-poster share card whenever a result is available
  useEffect(() => {
    if (!result) return;
    let alive = true;
    posterBlobUrl(result, result.images || {}, playerName)
      .then((p) => alive && setPoster(p))
      .catch(() => {});
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result]);

  const header = (
    <>
      <div className={styles.topBar}>
        <div className={styles.topBarIcon}>🎬</div>
        <div>
          <div className={styles.topBarTitle}>GREENLIGHT</div>
          <div className={styles.topBarSub}>Crossover Games</div>
        </div>
      </div>
      <div className="sport-picker">
        <div className="links">
          {sports.map((sport) => (
            <Link
              key={sport.id}
              className={`sportLink ${sport.id === "21" ? "prime-gaming-logo" : ""}`}
              to={sport.path}
            >
              {sport.emoji.startsWith("http") ? (
                <img src={sport.emoji} alt={sport.name} />
              ) : (
                <span>{sport.emoji}</span>
              )}
              <br />
              {sport.name.length < 4 ? (
                <span>{sport.name}</span>
              ) : (
                <span style={{ fontSize: 12 }}>{sport.name}</span>
              )}
            </Link>
          ))}
        </div>
      </div>
    </>
  );

  if (error) {
    return (
      <div className={styles.page}>
        {header}
        <div className={styles.wrap}>
          <div className={styles.error}>{error}</div>
        </div>
      </div>
    );
  }
  if (!daily) {
    return (
      <div className={styles.page}>
        {header}
        <div className={styles.loading}>Loading today's draft…</div>
      </div>
    );
  }

  const { slots, rounds } = daily;
  const currentRound = rounds[round];
  const draftDone = round >= rounds.length;

  const handlePick = (asset) => {
    const slotId = slotForAsset(asset, picks, slots);
    if (!slotId) return;
    setPicks((p) => ({ ...p, [slotId]: asset }));
    setPickOrder((o) => [...o, { slot: slotId, assetId: asset.id }]);
    setRound((r) => r + 1);
    setSpun(false); // next round starts back at the mystery card
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const body = {
        player_id: playerId,
        player_name: playerName.trim() || undefined,
        picks: pickOrder, // server validates pick i against round i's movie
      };
      if (playerName.trim())
        localStorage.setItem("greenlight_player_name", playerName.trim());
      const res = await fetch(`${API}/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Submission failed");
      // keep the picked assets' headshots so the share poster can use them
      data.images = Object.fromEntries(
        slots.filter((s) => picks[s.id]?.img).map((s) => [s.id, picks[s.id].img])
      );
      localStorage.setItem(
        `greenlight_result_${daily.date}`,
        JSON.stringify(data)
      );
      setResult(data);
      setTab("results");
    } catch (e) {
      setSubmitError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const shareText = () => {
    const r = result.results;
    const m = result.movie;
    return [
      `🎬 GREENLIGHT ${daily.date}`,
      ``,
      `${r.tier ? `[${r.tier} Tier] ` : ""}${r.archetype} — Score: ${r.movieScore}/100`,
      `🌍 ${money(r.worldwideGross)} worldwide`,
      `🏆 ${r.oscarWins} Oscar win${r.oscarWins === 1 ? "" : "s"} (${r.oscarNominations} noms)`,
      `🍅 Critics ${r.criticScore} · 🍿 Audience ${r.audienceScore}`,
      ``,
      `Dir: ${m.director.name} · Lead: ${m.lead.name}`,
      ``,
      `Challenge my movie 👉 crossover.games`,
    ].join("\n");
  };

  const copyShare = async () => {
    try {
      await navigator.clipboard.writeText(shareText());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  };

  const slotRow = (s, i, value) => (
    <div key={s.id} className={styles.slotRow}>
      <div
        className={`${styles.slotBadge} ${value ? styles.slotBadgeFilled : ""}`}
      >
        {value ? "✓" : `#${i + 1}`}
      </div>
      <div className={styles.slotLabel}>
        <span className={styles.slotLabelSub}>{s.label}</span>
        {value ? (
          <span className={styles.slotValue}>{value}</span>
        ) : (
          <span className={styles.slotEmpty}>Any</span>
        )}
      </div>
    </div>
  );

  const renderSlate = (movie) => (
    <div className={styles.slate}>
      <p className={styles.slateTitle}>Your Movie</p>
      {slots.map((s, i) =>
        slotRow(s, i, movie ? movie[s.id]?.name : picks[s.id]?.name)
      )}
    </div>
  );

  const renderDraft = () => {
    if (result) {
      return (
        <div className={styles.centerCard}>
          <p>You already greenlit a movie today. 🎬</p>
          <button className={styles.bigBtn} onClick={() => setTab("results")}>
            VIEW YOUR RESULTS
          </button>
        </div>
      );
    }
    if (draftDone) {
      return (
        <>
          {renderSlate()}
          <input
            className={styles.nameInput}
            placeholder="Your name (optional, for the leaderboard)"
            value={playerName}
            maxLength={24}
            onChange={(e) => setPlayerName(e.target.value)}
          />
          {submitError && <div className={styles.error}>{submitError}</div>}
          <button
            className={styles.bigBtn}
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? "ROLLING CAMERAS…" : "GREENLIGHT IT"}
          </button>
        </>
      );
    }
    return (
      <>
        <div className={styles.banner}>
          <p className={styles.bannerGoal}>
            🎯 Draft one asset from each movie to build a hit
          </p>
          <p className={styles.bannerRound}>
            Round {currentRound.round} of {rounds.length}
          </p>
        </div>
        <div className={styles.progress}>
          {rounds.map((r, i) => (
            <div
              key={r.round}
              className={`${styles.progressDot} ${
                i < round
                  ? styles.progressDone
                  : i === round
                    ? styles.progressCurrent
                    : ""
              }`}
            />
          ))}
        </div>
        {!spun ? (
          <div className={styles.spinStage}>
            <div className={styles.roundReel}>
              <span className={styles.roundReelValue}>
                {currentRound.round}
              </span>
              <span className={styles.roundReelLabel}>Round</span>
            </div>
            <p className={styles.spinLabel}>Spin to reveal movie</p>
            <div className={styles.mysteryCard}>
              <span>🎰</span>
              <span className={styles.mysteryQ}>?</span>
            </div>
            <button className={styles.spinBtn} onClick={() => setSpun(true)}>
              SPIN
            </button>
          </div>
        ) : (
          <>
            <div className={styles.movieTitleBlock}>
              <p className={styles.roundLabel}>Now Showing</p>
              <TitleReel
                title={currentRound.movie.title}
                year={currentRound.movie.year}
                poster={currentRound.movie.poster}
                pool={rounds.map((r) => r.movie.title)}
                started={spun}
              />
              <p className={styles.pickHint}>Pick one asset to fill a slot 🎬</p>
            </div>
            <div className={styles.assetList}>
              {currentRound.assets.map((a, i) => (
                <SpinAssetRow
                  key={`${round}-${a.id}`}
                  asset={a}
                  pool={rounds.flatMap((r) =>
                    r.assets.filter((x) => x.type === a.type).map((x) => x.name)
                  )}
                  delay={800 + i * 120} // staggered reel stops; tighter now that rounds have ~11 assets
                  started={spun}
                  target={slotForAsset(a, picks, slots)}
                  onPick={handlePick}
                  typeTag={`${a.type}${
                    a.type === "actor" && a.gender
                      ? a.gender === "f"
                        ? " (F)"
                        : " (M)"
                      : ""
                  }`}
                />
              ))}
            </div>
          </>
        )}
        {renderSlate()}
      </>
    );
  };

  const renderResults = () => {
    if (!result) {
      return (
        <div className={styles.centerCard}>
          <p>You haven't drafted today's movie yet.</p>
          <button className={styles.bigBtn} onClick={() => setTab("draft")}>
            START DRAFTING
          </button>
        </div>
      );
    }
    const r = result.results;
    const m = result.movie;
    return (
      <div className={styles.resultCard}>
        <p className={styles.roundLabel}>{daily.date}</p>
        {r.tier && (
          <div
            className={styles.tierBadge}
            style={{
              background: TIER_COLORS[r.tier] || "#9ca3af",
              color: r.tier === "S" ? "#111" : "#fff",
            }}
          >
            {r.tier}
          </div>
        )}
        <div className={styles.archetype}>{r.archetype}</div>
        <div className={styles.scoreCircle}>
          <span className={styles.scoreNum}>{r.movieScore}</span>
          <span className={styles.scoreLbl}>Movie Score</span>
        </div>
        <div className={styles.statGrid}>
          <div className={styles.stat}>
            <div className={styles.statVal}>{money(r.openingWeekend)}</div>
            <div className={styles.statLbl}>Opening</div>
          </div>
          <div className={styles.stat}>
            <div className={styles.statVal}>{money(r.worldwideGross)}</div>
            <div className={styles.statLbl}>Worldwide</div>
          </div>
          <div className={styles.stat}>
            <div className={styles.statVal}>{money(r.profit)}</div>
            <div className={styles.statLbl}>Profit</div>
          </div>
          <div className={styles.stat}>
            <div className={styles.statVal}>{r.criticScore}</div>
            <div className={styles.statLbl}>Critics</div>
          </div>
          <div className={styles.stat}>
            <div className={styles.statVal}>{r.audienceScore}</div>
            <div className={styles.statLbl}>Audience</div>
          </div>
          <div className={styles.stat}>
            <div className={styles.statVal}>
              {r.oscarWins}/{r.oscarNominations}
            </div>
            <div className={styles.statLbl}>Oscars W/N</div>
          </div>
        </div>
        {r.bestPicture?.won && (
          <div className={styles.bestPicture}>
            🏆 WON Best Picture
          </div>
        )}
        {r.bestPicture?.nominated && !r.bestPicture.won && (
          <div className={styles.bestPicture}>
            🏆 Nominated for Best Picture
            {r.bestPicture.lostTo ? ` — lost to ${r.bestPicture.lostTo}` : ""}
          </div>
        )}
        {r.chemistryHits?.map((c, i) => (
          <div key={i} className={styles.chemHit}>
            ✨ {typeof c === "string" ? c : c.name || JSON.stringify(c)}
          </div>
        ))}
        <div style={{ marginTop: 16, textAlign: "left" }}>
          {renderSlate(m)}
        </div>
        {poster && (
          <>
            <img
              className={styles.posterPreview}
              src={poster.url}
              alt="Your movie poster"
            />
            <a
              className={styles.bigBtn}
              style={{ textDecoration: "none", textAlign: "center" }}
              href={poster.url}
              download={`greenlight-${daily.date}.png`}
            >
              DOWNLOAD POSTER
            </a>
          </>
        )}
        <button className={styles.bigBtn} onClick={copyShare}>
          {copied ? "COPIED! 📋" : "SHARE MY MOVIE"}
        </button>
        <a
          className={styles.unlimitedCta}
          href={migrationUrl("greenlight_unlimited")}
          target="_blank"
          rel="noopener noreferrer"
        >
          🎰 Want more? Play <strong>Unlimited Greenlight</strong> on
          crossover.games →
        </a>
        <MigrationModal gameKey="greenlight" medium="greenlight_post_game" />
      </div>
    );
  };

  const METRICS = [
    ["score", "Score"],
    ["gross", "Gross"],
    ["profit", "Profit"],
    ["oscars", "Oscars"],
    ["critic", "Critics"],
    ["audience", "Audience"],
  ];

  const metricValue = (entry) => {
    switch (metric) {
      case "gross":
        return money(Number(entry.worldwide_gross));
      case "profit":
        return money(Number(entry.profit));
      case "oscars":
        return `${entry.oscar_wins}🏆`;
      case "critic":
        return entry.critic_score;
      case "audience":
        return entry.audience_score;
      default:
        return entry.movie_score;
    }
  };

  const renderLeaderboard = () => (
    <>
      <div className={styles.metricRow}>
        {METRICS.map(([k, label]) => (
          <button
            key={k}
            className={`${styles.metricBtn} ${metric === k ? styles.metricActive : ""}`}
            onClick={() => setMetric(k)}
          >
            {label}
          </button>
        ))}
      </div>
      {!leaderboard ? (
        <div className={styles.loading}>Loading…</div>
      ) : leaderboard.leaderboard.length === 0 ? (
        <div className={styles.loading}>No movies greenlit yet today.</div>
      ) : (
        <table className={styles.lbTable}>
          <thead>
            <tr>
              <th>#</th>
              <th>Producer</th>
              <th>Movie</th>
              <th>Tier</th>
              <th>{METRICS.find(([k]) => k === metric)[1]}</th>
            </tr>
          </thead>
          <tbody>
            {leaderboard.leaderboard.map((e) => (
              <tr
                key={e.id}
                className={e.player_id === playerId ? styles.lbMe : ""}
              >
                <td>{e.rank}</td>
                <td>{e.player_name || "Anonymous"}</td>
                <td>
                  {e.movie?.director?.name} / {e.movie?.lead?.name}
                </td>
                <td>
                  {e.tier && (
                    <span
                      className={styles.lbTier}
                      style={{
                        background: TIER_COLORS[e.tier] || "#9ca3af",
                        color: e.tier === "S" ? "#111" : "#fff",
                      }}
                    >
                      {e.tier}
                    </span>
                  )}
                </td>
                <td>{metricValue(e)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );

  return (
    <div className={styles.page}>
      {header}
      <div className={styles.wrap}>
        <div className={styles.tabs}>
          {[
            ["draft", "Draft"],
            ["results", "Results"],
            ["leaderboard", "Leaderboard"],
          ].map(([k, label]) => (
            <button
              key={k}
              className={`${styles.tab} ${tab === k ? styles.tabActive : ""}`}
              onClick={() => setTab(k)}
            >
              {label}
            </button>
          ))}
        </div>
        {tab === "draft" && renderDraft()}
        {tab === "results" && renderResults()}
        {tab === "leaderboard" && renderLeaderboard()}
      </div>
    </div>
  );
}
